/**
 * TableEmpty / TableSkeleton — the two states a data table spends real time in.
 *
 * These live in `src/components/` rather than in `src/ui/table.tsx` ON PURPOSE.
 * `table` is on the shadcn re-import allowlist in
 * `scripts/import-from-shadcn.mjs`, and that script's contract is that
 * `src/ui/` stays byte-for-byte identical to upstream so visual diffs against
 * shadcn stay reviewable. Anything added there is silently overwritten on the
 * next re-import. So they compose the shadcn parts from the house tier instead,
 * which is also the layering the rest of the library already follows.
 *
 * Both render a real `<tr>` and are meant to sit inside `<TableBody>`, so the
 * table keeps one column model and the browser keeps one table layout:
 *
 *   <Table>
 *     <TableHeader>…</TableHeader>
 *     <TableBody>
 *       {loading ? (
 *         <TableSkeleton columns={4} />
 *       ) : rows.length === 0 ? (
 *         <TableEmpty colSpan={4}>No invoices yet.</TableEmpty>
 *       ) : (
 *         rows.map(…)
 *       )}
 *     </TableBody>
 *   </Table>
 *
 * That `loading ? … : empty ? … : rows` order is the whole point of shipping
 * these together: a table that shows "no results" over a request still in
 * flight is the single most common way this pair gets told wrong.
 */
import * as React from "react";
import { TableCell, TableRow } from "../ui/table";
import { Skeleton } from "../primitives/Skeleton";
import { cn } from "../lib/cn";

/* ============================================================================
 * TableEmpty
 * ========================================================================= */

export interface TableEmptyProps extends React.ComponentProps<"td"> {
  /**
   * Number of columns to span. REQUIRED — a `<td>` that doesn't span the full
   * header leaves the message wedged under column one with the rest of the row
   * blank, which reads as a broken row rather than an empty table.
   */
  colSpan: number;
  /** The message. Defaults to a neutral line rather than a branded one. */
  children?: React.ReactNode;
  /** Height of the empty row. Default `"h-24"` — roughly three data rows. */
  className?: string;
}

export const TableEmpty = React.forwardRef<HTMLTableCellElement, TableEmptyProps>(
  function TableEmpty({ colSpan, children = "No results.", className, ...props }, ref) {
    return (
      <TableRow
        // The empty row is not data and must not offer the row hover
        // affordance — there is nothing here to select or open.
        className="hover:bg-transparent"
      >
        <TableCell
          ref={ref}
          colSpan={colSpan}
          className={cn("h-24 text-center align-middle text-muted-foreground", className)}
          {...props}
        >
          {children}
        </TableCell>
      </TableRow>
    );
  },
);

/* ============================================================================
 * TableSkeleton
 * ========================================================================= */

export interface TableSkeletonProps {
  /** Column count. Must match the header, or the layout shifts when data lands. */
  columns: number;
  /** Placeholder row count. Default 5. */
  rows?: number;
  /** Class for each placeholder bar. */
  className?: string;
}

/**
 * Deterministic width ladder, indexed by column. Two reasons it is a constant
 * and not `Math.random()`: a random width re-rolls on every re-render, so the
 * bars twitch while the fetch is in flight; and a random width is not stable
 * across a server render and its hydration.
 */
const CELL_WIDTHS = ["w-[70%]", "w-[45%]", "w-[60%]", "w-[35%]", "w-[55%]", "w-[50%]"] as const;

export function TableSkeleton({ columns, rows = 5, className }: TableSkeletonProps) {
  const cols = Math.max(1, columns);
  return (
    <>
      {Array.from({ length: Math.max(1, rows) }).map((_, r) => (
        <TableRow key={r} aria-hidden="true" className="hover:bg-transparent">
          {Array.from({ length: cols }).map((_, c) => (
            <TableCell key={c}>
              <Skeleton
                variant="text"
                animation="shimmer"
                className={cn("h-3.5", CELL_WIDTHS[c % CELL_WIDTHS.length], className)}
              />
            </TableCell>
          ))}
        </TableRow>
      ))}
    </>
  );
}
